// Mon profil : nom affiché, informations du compte en lecture seule, changement de mot de passe (comptes locaux).

import {
  PasswordChangeSchema,
  ProfileSchema,
  entityBySync,
  visibleSyncEntities,
  type Me,
} from '@cockpit/shared';
import { Avatar, Button, Card, CardHeader, ErrorState, Field, Input, Notice, Spinner } from '@cockpit/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { PageHeader } from '../components/Layout.js';
import { api, ApiError } from '../lib/api.js';
import { ROLE_LABEL } from '../lib/labels.js';
import { useMe } from '../lib/queries.js';

function entitiesLabel(me: Me): string {
  const visible = visibleSyncEntities(me);
  if (!visible) return 'Toutes les entités';
  if (!visible.length) return 'Aucune entité attribuée';
  return visible.map((e) => entityBySync(e)?.label ?? e).join(', ');
}

function Success({ children }: { children: string }) {
  return (
    <p role="status" className="text-[13.5px] font-semibold text-success-700">
      {children}
    </p>
  );
}

function IdentityCard({ me }: { me: Me }) {
  const qc = useQueryClient();
  const [name, setName] = useState(me.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const unchanged = name.trim() === me.name;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = ProfileSchema.safeParse({ name });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Nom invalide.');
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api<Me>('/api/auth/profile', { method: 'PATCH', body: parsed.data });
      qc.setQueryData(['me'], updated);
      setName(updated.name);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  const rows: [string, string][] = [
    ['Adresse e-mail', me.email],
    ['Rôle', ROLE_LABEL[me.role]],
    ['Entités visibles', entitiesLabel(me)],
    ['Connexion', me.provider === 'entra' ? 'Compte Microsoft' : 'Compte local'],
  ];

  return (
    <Card>
      <CardHeader eyebrow="Compte" title="Informations" />
      <div className="mb-6 flex items-center gap-4">
        <Avatar name={me.name} size={48} />
        <div className="min-w-0">
          <p className="truncate font-display text-[17px] font-bold text-ink">{me.name}</p>
          <p className="truncate text-[13.5px] text-ink-3">{me.email}</p>
        </div>
      </div>

      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label="Nom affiché" hint="Visible des autres utilisateurs, par exemple dans « traité par ».">
          {(id) => (
            <Input
              id={id}
              value={name}
              maxLength={80}
              autoComplete="name"
              onChange={(e) => {
                setName(e.target.value);
                setSaved(false);
              }}
            />
          )}
        </Field>
        {error && <ErrorState title="Enregistrement impossible">{error}</ErrorState>}
        <div className="flex items-center gap-4">
          <Button type="submit" disabled={busy || unchanged}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          {saved && <Success>Profil enregistré.</Success>}
        </div>
      </form>

      <dl className="mt-6 divide-y divide-line-soft border-t border-line-soft text-[14px]">
        {rows.map(([k, v]) => (
          <div key={k} className="grid gap-1 py-3 sm:grid-cols-[170px_minmax(0,1fr)] sm:gap-4">
            <dt className="text-ink-3">{k}</dt>
            <dd className="font-semibold text-ink [overflow-wrap:anywhere]">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="text-[12.5px] text-ink-3">
        L’adresse e-mail, le rôle et les entités sont gérés par un administrateur du cockpit.
      </p>
    </Card>
  );
}

function PasswordCard({ me }: { me: Me }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (me.provider !== 'local' || me.authMode !== 'app') {
    return (
      <Card>
        <CardHeader eyebrow="Sécurité" title="Mot de passe" />
        <Notice icon={<Info size={18} strokeWidth={1.75} />}>
          Votre connexion passe par votre compte Microsoft : le mot de passe se change dans votre compte
          Microsoft, pas ici.
        </Notice>
      </Card>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    const parsed = PasswordChangeSchema.safeParse({ current, next });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Saisie invalide.');
    if (next !== confirm) return setError('La confirmation ne correspond pas au nouveau mot de passe.');
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/password', { method: 'POST', body: parsed.data });
      setCurrent('');
      setNext('');
      setConfirm('');
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Changement impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader eyebrow="Sécurité" title="Mot de passe" />
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label="Mot de passe actuel">
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Nouveau mot de passe"
          hint="12 caractères minimum, avec au moins trois types parmi minuscules, majuscules, chiffres et symboles."
        >
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </Field>
        <Field label="Confirmation du nouveau mot de passe">
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          )}
        </Field>
        {error && <ErrorState title="Changement impossible">{error}</ErrorState>}
        <div className="flex items-center gap-4">
          <Button type="submit" disabled={busy || !current || !next || !confirm}>
            {busy ? 'Enregistrement…' : 'Changer le mot de passe'}
          </Button>
          {saved && <Success>Mot de passe modifié.</Success>}
        </div>
      </form>
    </Card>
  );
}

export function ProfilePage() {
  const me = useMe();
  if (me.isLoading || !me.data) return <Spinner />;
  return (
    <div className="flex flex-col gap-2">
      <PageHeader
        eyebrow="Compte"
        title="Mon profil"
        sub="Votre nom affiché et la sécurité de votre accès."
      />
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <IdentityCard me={me.data} />
        <PasswordCard me={me.data} />
      </div>
    </div>
  );
}
